import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash2, Mail, Shield, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const v = (email || "").trim().toLowerCase();
    if (!v || !v.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      const { data } = await api.post("/admin/users", { email: v });
      if (data.already) toast.message("Email already invited");
      else toast.success("User invited");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (em) => {
    if (!window.confirm(`Revoke access for ${em}?`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(em)}`);
      toast.success("Access revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (denied || (user && user.role !== "admin")) {
    return (
      <div className="brut-card p-10 text-center max-w-md mx-auto" data-testid="admin-denied">
        <Shield className="mx-auto mb-3 text-[#E63946]" size={36} strokeWidth={2.5} />
        <div className="font-heading font-black text-2xl">ADMIN ONLY</div>
        <div className="text-sm text-[#666] mt-2 uppercase tracking-wider">This page is restricted to admin accounts.</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="admin-users-page">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-[#E63946]" strokeWidth={3} />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-1">── ACCESS CONTROL</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">USERS.</h1>
        </div>
      </div>

      <div className="brut-card p-5 mb-6">
        <label className="label-mono">Invite Gmail address</label>
        <div className="flex gap-2">
          <input
            data-testid="invite-email-input"
            className="brut-input"
            type="email"
            value={email}
            placeholder="agent2@gmail.com"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <button onClick={add} className="brut-btn brut-btn-primary" data-testid="invite-add-btn">
            <UserPlus size={16} strokeWidth={3} /> INVITE
          </button>
        </div>
        <div className="text-xs text-[#666] uppercase tracking-wider mt-2">
          Only invited Gmail accounts can sign in.
        </div>
      </div>

      <div className="brut-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase">Email</th>
              <th className="px-4 py-3 text-xs uppercase">Role</th>
              <th className="px-4 py-3 text-xs uppercase">Status</th>
              <th className="px-4 py-3 text-xs uppercase">Name</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-[#666]">Loading…</td></tr>)}
            {!loading && users.length === 0 && (<tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-[#666] uppercase tracking-wider">No invited users yet</td></tr>)}
            {users.map((u) => (
              <tr key={u.email} className="border-b border-black/15" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono"><Mail className="inline mr-2" size={12} />{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 border-2 border-black font-bold uppercase
                    ${u.role === "admin" ? "bg-[#E63946] text-white" : "bg-white"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  {u.registered
                    ? <span className="text-xs font-bold text-[#2A9D8F]"><Check className="inline" size={14} strokeWidth={3} /> ACTIVE</span>
                    : <span className="text-xs font-bold text-[#666]"><X className="inline" size={14} strokeWidth={3} /> PENDING</span>}
                </td>
                <td className="px-4 py-3 text-sm">{u.name || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "admin" && (
                    <button onClick={() => remove(u.email)} className="text-[#E63946]" data-testid={`remove-user-${u.email}`}>
                      <Trash2 size={16} strokeWidth={3} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
